import { createSlice, current } from '@reduxjs/toolkit';
import initialState from './initialState';
import {
  closeLeads,
  getAgentsForLeads,
  getClosingExcuses,
  getLeads,
  getLeadsStatus,
  getLeadsTags,
  loadMoreLeads,
  setLeadComment,
  setMassLeadStatus,
} from './services';
import { sortContacts } from './utils';
import {
  admitToPool,
  applyResultToColumns,
  recomputeCanClaim,
  usesPoolCard,
} from './sharkTankRules';

function headerDelta(sourceBlock, targetBlock) {
  const left = usesPoolCard(sourceBlock) ? 0 : 1;
  const entered = usesPoolCard(targetBlock) ? 0 : 1;
  return entered - left;
}

function addToColumn(column, contacts, currentUserId) {
  const admitted = admitToPool(
    column.lead_status.shark_tank,
    contacts,
    currentUserId
  );
  const merged = sortContacts([...column.contacts, ...admitted.contacts]);
  if (!usesPoolCard(admitted.block)) {
    column.contacts = merged;
    return;
  }
  column.lead_status.shark_tank = admitted.block;
  column.contacts = recomputeCanClaim(merged, admitted.block);
}

export const LeadsSlice = createSlice({
  name: 'Leads',
  initialState,
  reducers: {
    toggleLeadSelected: (state, action) => {
      const { leadId, key } = action.payload;
      if (state[key].includes(leadId)) {
        state[key] = state[key].filter((id) => id !== leadId);
      } else {
        state[key].push(leadId);
      }
    },

    selectRenderedLeads: (state, action) => {
      const ids = action.payload?.ids || [];
      const allSelected =
        ids.length > 0 && ids.every((id) => state.leadsSelected.includes(id));
      if (allSelected) {
        state.leadsSelected = state.leadsSelected.filter(
          (id) => !ids.includes(id)
        );
        return;
      }
      ids.forEach((id) => {
        if (!state.leadsSelected.includes(id)) state.leadsSelected.push(id);
      });
    },

    removeSelectedLeads: (state, action) => {
      const ids = action.payload?.ids || [];
      if (ids.length === 0) return;
      state.leadsSelected = state.leadsSelected.filter(
        (id) => !ids.includes(id)
      );
    },

    setSearch: (state, action) => {
      const { value, item } = action.payload;

      state.search.value = value;
      state.search.item = item || state.search.item;
    },

    setChangeStatusModal: (state, action) => {
      const { key, value } = action.payload;

      state.changeStatusModal[key] = value;
    },

    setCloseLeadModal: (state, action) => {
      const { key, value } = action.payload;

      state.closeLeadModal[key] = value;

      if (key === 'selectingTags') {
        state.closeLeadModal.tagsToAdd = value
          ? [...state.closeLeadModal.tags]
          : [];
      }

      if (key === 'selectedClosingExcuse') {
        state.closeLeadModal.tags = state.closeLeadModal.tags.map((tag) => ({
          ...tag,
          checked: false,
        }));
      }
    },

    dismissCloseLeadModal: (state) => {
      if (state.closeLeadModal.closingLeads) return;

      if (state.closeLeadModal.selectingTags) {
        state.closeLeadModal.selectingTags = false;
        state.closeLeadModal.tagsToAdd = [];
        return;
      }

      state.closeLeadModal.isOpen = false;
    },

    resetCloseLeadModal: (state) => {
      state.closeLeadModal = {
        ...initialState.closeLeadModal,
        tags: state.closeLeadModal.tags,
      };
    },

    removeTagSelection: (state, action) => {
      const tagId = action.payload;

      state.closeLeadModal.tags = state.closeLeadModal.tags.map((tag) =>
        tag.id === tagId ? { ...tag, checked: false } : tag
      );
    },

    saveSelectedTags: (state) => {
      state.closeLeadModal.tags = state.closeLeadModal.tagsToAdd;
      state.closeLeadModal.tagsToAdd = [];
      state.closeLeadModal.selectingTags = false;
    },

    setReasignModal: (state, action) => {
      const { key, value } = action.payload;

      state.reasignModal[key] = value;
    },

    clearSelectedLeads: (state) => {
      state.leadsSelected = [];
      state.columnsSelected = [];
    },

    updateColumnSelection: (state, action) => {
      const { columnId, contactList } = action.payload;

      // Solo verificar selecciones si la columna tiene contactos
      const hasContacts = contactList.length > 0;
      const allContactsSelected =
        hasContacts &&
        contactList.every((contactId) =>
          state.leadsSelected.includes(contactId)
        );

      if (allContactsSelected && !state.columnsSelected.includes(columnId)) {
        state.columnsSelected.push(columnId);
      } else if (
        !allContactsSelected &&
        state.columnsSelected.includes(columnId)
      ) {
        state.columnsSelected = state.columnsSelected.filter(
          (id) => id !== columnId
        );
      }
    },

    setNotSelectedModal: (state, action) => {
      const { isOpen, key } = action.payload;

      state.notSelectedModal.isOpen = isOpen;
      state.notSelectedModal.key = key;
    },

    // Drag & Drop reducers
    setActiveCard: (state, action) => {
      state.activeCard = action.payload;
    },

    setSourceColumnId: (state, action) => {
      state.sourceColumnId = action.payload;
    },

    setTargetColumnId: (state, action) => {
      state.targetColumnId = action.payload;
    },

    clearDragState: (state) => {
      state.activeCard = null;
      state.sourceColumnId = null;
      state.targetColumnId = null;
    },

    // Move contact between columns (replaces handleDragEnd logic)
    moveContact: (state, action) => {
      const { activeId, sourceColumnId, targetColumnId, currentUserId } =
        action.payload;

      // Find source and target columns
      const sourceColumn = state.leadsData.find(
        (col) => col.lead_status.id === sourceColumnId
      );
      const targetColumn = state.leadsData.find(
        (col) => col.lead_status.id === targetColumnId
      );

      if (!sourceColumn || !targetColumn) {
        return;
      }

      // Find contact in source column
      const contactIndex = sourceColumn.contacts.findIndex(
        (contact) => contact.id === activeId
      );

      if (contactIndex === -1) {
        return;
      }

      // Remove from source and add to target
      const [movedContact] = sourceColumn.contacts.splice(contactIndex, 1);
      addToColumn(
        targetColumn,
        [
          {
            ...movedContact,
            freshness: 100,
            time_until_expired_status: null,
          },
        ],
        currentUserId
      );

      // Update contact counts
      sourceColumn.total_contacts -= 1;
      targetColumn.total_contacts += 1;
      state.totalLeads += headerDelta(
        sourceColumn.lead_status?.shark_tank,
        targetColumn.lead_status?.shark_tank
      );
    },

    toggleShowInput: (state, action) => {
      state.showSearch = action.payload;
    },

    // Update leadsData directly (replaces setLeadsData from context)
    updateLeadsData: (state, action) => {
      state.leadsData = action.payload;
    },

    toggleMassiveSelected: (state, action) => {
      const { columnId, list } = action.payload;

      const contactList = list.map((contact) => contact.id);

      if (state.columnsSelected.includes(columnId)) {
        state.columnsSelected = state.columnsSelected.filter(
          (id) => id !== columnId
        );

        state.leadsSelected = state.leadsSelected.filter(
          (id) => !contactList.includes(id)
        );
      } else {
        state.columnsSelected.push(columnId);

        const newContacts = contactList.filter(
          (contact) => !state.leadsSelected.includes(contact)
        );
        state.leadsSelected.push(...newContacts);
      }
    },

    setReassignContacts: (state, action) => {
      state.reassignContacts = action.payload;
    },

    // Claim and recover update the columns on screen. The header uses the
    // pool block already on the column: the response quota can come back null.
    applySharkTankResult: (state, action) => {
      const columnsNow = current(state.leadsData);
      const source = columnsNow.find(
        (column) => column.lead_status?.id === action.payload.statusId
      );
      const sourceBlock = source?.lead_status?.shark_tank;
      const { columns, removed, movedToVisibleColumn } = applyResultToColumns(
        columnsNow,
        {
          ...action.payload,
          sharkTank: action.payload.sharkTank ?? sourceBlock ?? null,
        }
      );
      state.leadsData = columns;
      if (removed > 0) {
        const poolOutsideHeader = usesPoolCard(sourceBlock);
        if (poolOutsideHeader && movedToVisibleColumn) {
          state.totalLeads += removed;
        } else if (!poolOutsideHeader && !movedToVisibleColumn) {
          state.totalLeads -= removed;
        }
      }
    },

    // Kept in memory only: the notice shows up again after a reload.
    dismissSharkTankNotice: (state, action) => {
      if (!state.dismissedSharkTankNotices.includes(action.payload)) {
        state.dismissedSharkTankNotices.push(action.payload);
      }
    },

    bumpOpportunitiesPanel: (state) => {
      state.opportunitiesRevision += 1;
    },

    // Common reducer to move leads to different status (extracted from extraReducer)
    moveLeadsToStatus: (state, action) => {
      const {
        ids,
        lead_status_id,
        clearSelections = true,
        time_until_expired_status,
        currentUserId,
      } = action.payload;
      const contactsMoved = [];
      const alreadyInTarget = new Set();
      let leftHeader = 0;

      // Find and remove contacts from all columns that match the provided IDs
      state.leadsData.forEach((column) => {
        const contactsToRemove = [];

        // Find contacts that match the provided IDs
        column.contacts.forEach((contact, index) => {
          if (ids.includes(contact.id)) {
            contactsToRemove.push({ contact, index });
            contactsMoved.push(contact);
            if (column.lead_status?.id === lead_status_id) {
              alreadyInTarget.add(contact.id);
            }
          }
        });

        if (
          contactsToRemove.length > 0 &&
          !usesPoolCard(column.lead_status?.shark_tank)
        ) {
          leftHeader += contactsToRemove.length;
        }

        // Remove found contacts from current column (in reverse order to not affect indices)
        contactsToRemove.reverse().forEach(({ index }) => {
          column.contacts.splice(index, 1);
        });

        // Update contact count for the column
        column.total_contacts -= contactsToRemove.length;
      });

      // Find target column and add all moved contacts
      const targetColumn = state.leadsData.find(
        (column) => column.lead_status.id === lead_status_id
      );

      let enteredHeader = 0;
      if (targetColumn && contactsMoved.length > 0) {
        const contactsMapped = contactsMoved.map((contact) => ({
          ...contact,
          freshness: 100,
          time_until_expired_status,
        }));
        const arriving = contactsMapped.filter(
          (contact) => !alreadyInTarget.has(contact.id)
        );
        const returning = contactsMapped.filter((contact) =>
          alreadyInTarget.has(contact.id)
        );

        // A drag already admitted the card. Admitting it again would raise
        // the pool's pending recoveries a second time.
        if (arriving.length > 0) {
          addToColumn(targetColumn, arriving, currentUserId);
        }
        if (returning.length > 0) {
          targetColumn.contacts = sortContacts([
            ...targetColumn.contacts,
            ...returning,
          ]);
        }

        // Update contact count for target column
        targetColumn.total_contacts += ids.length;
        if (!usesPoolCard(targetColumn.lead_status?.shark_tank)) {
          enteredHeader = contactsMoved.length;
        }
      }

      state.totalLeads += enteredHeader - leftHeader;

      // Clear selections after mass movement (optional)
      if (clearSelections) {
        state.leadsSelected = [];
        state.columnsSelected = [];
      }
    },

    setContactTags: (state, action) => {
      const { id, tags } = action.payload;
      (state.leadsData || []).forEach((column) => {
        const row = column.contacts?.find((contact) => contact.id === id);
        if (row) row.tags = tags;
      });
    },
  },
  extraReducers: (builder) => {
    // New getLeadsData thunk (replaces context logic)
    builder.addCase(getLeads.pending, (state, action) => {
      state.tableRequestId = action.meta.requestId;
      if (action.meta.arg.background) return;
      state.loading = true;
      state.error = null;
    });

    builder.addCase(getLeads.fulfilled, (state, action) => {
      // Only the latest request writes the table: a slower, older one would
      // bring back a previous flow or search.
      if (action.meta.requestId !== state.tableRequestId) return;
      state.loading = false;
      state.error = null;

      const mappedLeadsData = action.payload.leads_data.map((lead) => ({
        ...lead,
        page: 1,
      }));

      state.leadsData = mappedLeadsData || [];
      state.totalLeads = action.payload.total_leads || 0;
      state.leadsFlowId = action.meta.arg.flowId ?? null;
      state.leadsRequestId = action.meta.requestId;
    });

    builder.addCase(getLeads.rejected, (state, action) => {
      if (action.meta.requestId !== state.tableRequestId) return;
      // A failed background refresh keeps the board that already loaded.
      if (action.meta.arg.background && !state.loading && !state.error) return;
      state.loading = false;
      state.error = action.error;
      state.leadsData = [];
      state.totalLeads = 0;
      state.leadsFlowId = action.meta.arg.flowId ?? null;
      state.leadsRequestId = action.meta.requestId;
    });

    // Load more leads
    builder.addCase(loadMoreLeads.fulfilled, (state, action) => {
      // A page asked for by a table that was replaced since then. It is
      // compared with the table on screen, not the latest request: a
      // background refresh keeps the old rows visible while it is pending.
      if (action.meta.arg.leadsRequestId !== state.leadsRequestId) return;
      const columnIndex = state.leadsData.findIndex(
        (lead) => lead.lead_status.id === action.meta.arg.leadStatusId
      );

      if (columnIndex !== -1) {
        // Get existing contact IDs to avoid duplicates
        const existingContactIds = new Set(
          state.leadsData[columnIndex].contacts.map((contact) => contact.id)
        );

        // Filter new contacts to exclude duplicates
        const newUniqueContacts = action.payload.results.filter(
          (contact) => !existingContactIds.has(contact.id)
        );

        // Add only unique contacts
        state.leadsData[columnIndex].contacts = [
          ...state.leadsData[columnIndex].contacts,
          ...newUniqueContacts,
        ];
        state.leadsData[columnIndex].page += 1;
        state.leadsData[columnIndex].has_more = Boolean(action.payload.next);
      }
    });

    // update contact comment
    builder.addCase(setLeadComment.pending, (state) => {
      state.loadingComments = true;
    });

    builder.addCase(setLeadComment.fulfilled, (state, action) => {
      state.loadingComments = false;
      const { id, comment, columnId } = action.meta.arg;
      const columnIndex = state.leadsData.findIndex(
        (lead) => lead.lead_status.id === columnId
      );

      if (columnIndex === -1) return;
      const row = state.leadsData[columnIndex].contacts?.find(
        (contact) => contact.id === id
      );
      if (row) row.last_comment = comment;
    });

    builder.addCase(setLeadComment.rejected, (state) => {
      state.loadingComments = false;
    });

    // set mass lead status
    builder.addCase(setMassLeadStatus.pending, (state) => {
      state.changeStatusModal.loading = true;
    });

    builder.addCase(setMassLeadStatus.rejected, (state) => {
      state.changeStatusModal.loading = false;
    });

    builder.addCase(setMassLeadStatus.fulfilled, (state, action) => {
      state.changeStatusModal.loading = false;

      const { ids, lead_status_id, currentUserId } = action.meta.arg;
      const { time_until_expired_status } = action.payload;

      // Use the common reducer to handle the mass movement logic
      LeadsSlice.caseReducers.moveLeadsToStatus(state, {
        payload: {
          ids,
          lead_status_id,
          clearSelections: true,
          time_until_expired_status,
          currentUserId,
        },
      });
    });

    // get leads status
    builder.addCase(getLeadsStatus.fulfilled, (state, action) => {
      state.changeStatusModal.leadList = action.payload;
    });

    // get agents for leads
    builder.addCase(getAgentsForLeads.pending, (state) => {
      state.reasignModal.loading = true;
    });

    builder.addCase(getAgentsForLeads.fulfilled, (state, action) => {
      state.reasignModal.agentsList = action.payload.users;
      state.reasignModal.loading = false;
    });

    // get tags for leads
    builder.addCase(getLeadsTags.pending, (state) => {
      state.closeLeadModal.loadingTags = true;
    });

    builder.addCase(getLeadsTags.fulfilled, (state, action) => {
      state.closeLeadModal.loadingTags = false;
      state.closeLeadModal.tags = action.payload;
    });

    // get closing excuses
    builder.addCase(getClosingExcuses.pending, (state) => {
      state.closeLeadModal.loadingClosingExcuses = true;
    });

    builder.addCase(getClosingExcuses.fulfilled, (state, action) => {
      state.closeLeadModal.loadingClosingExcuses = false;
      state.closeLeadModal.closingExcuses = action.payload;
    });

    // close leads
    builder.addCase(closeLeads.pending, (state) => {
      state.closeLeadModal.closingLeads = true;
    });

    builder.addCase(closeLeads.rejected, (state) => {
      state.closeLeadModal.closingLeads = false;
    });

    builder.addCase(closeLeads.fulfilled, (state) => {
      let leftHeader = 0;
      state.leadsData = state.leadsData.map((column) => {
        const filteredContacts = column.contacts.filter(
          (contact) => !state.leadsSelected.includes(contact.id)
        );
        const countRemovedFromColumn =
          column.contacts.length - filteredContacts.length;
        if (
          countRemovedFromColumn > 0 &&
          !usesPoolCard(column.lead_status?.shark_tank)
        ) {
          leftHeader += countRemovedFromColumn;
        }

        return {
          ...column,
          contacts: filteredContacts,
          total_contacts: column.total_contacts - countRemovedFromColumn,
        };
      });
      state.totalLeads -= leftHeader;

      LeadsSlice.caseReducers.clearSelectedLeads(state, {
        type: 'LeadsSlice/clearSelectedLeads',
      });
      LeadsSlice.caseReducers.resetCloseLeadModal(state, {
        type: 'LeadsSlice/resetCloseLeadModal',
      });
    });
  },
});

export const {
  toggleLeadSelected,
  selectRenderedLeads,
  removeSelectedLeads,
  toggleMassiveSelected,
  updateColumnSelection,
  setActiveCard,
  setSourceColumnId,
  setTargetColumnId,
  clearDragState,
  moveContact,
  updateLeadsData,
  setNotSelectedModal,
  setChangeStatusModal,
  moveLeadsToStatus,
  setReasignModal,
  clearSelectedLeads,
  setReassignContacts,
  bumpOpportunitiesPanel,
  applySharkTankResult,
  dismissSharkTankNotice,
  setCloseLeadModal,
  dismissCloseLeadModal,
  resetCloseLeadModal,
  setContactTags,
  removeTagSelection,
  saveSelectedTags,
  setSearch,
  toggleShowInput,
} = LeadsSlice.actions;

export default LeadsSlice.reducer;
